import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Palette, Globe } from 'lucide-react';
import { useUserPreferences, useUpdateUserPreferences } from '@/hooks/useUserPreferences';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { useEffect } from 'react';

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
  { value: 'de', label: 'Deutsch' },
  { value: 'pt', label: 'Português' },
  { value: 'sw', label: 'Kiswahili' },
];

const THEMES = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

export function AppearanceSettings() {
  const { data: prefs, isLoading } = useUserPreferences();
  const updatePrefs = useUpdateUserPreferences();

  // Apply theme on load and change
  useEffect(() => {
    if (!prefs?.theme) return;
    const root = document.documentElement;
    if (prefs.theme === 'dark') {
      root.classList.add('dark');
    } else if (prefs.theme === 'light') {
      root.classList.remove('dark');
    } else {
      // system
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark', prefersDark);
    }
  }, [prefs?.theme]);

  const handleThemeChange = (value: string) => {
    updatePrefs.mutate({ theme: value }, {
      onSuccess: () => toast.success('Theme updated'),
      onError: () => toast.error('Failed to update theme'),
    });
  };

  const handleLanguageChange = (value: string) => {
    updatePrefs.mutate({ language: value }, {
      onSuccess: () => toast.success('Language updated'),
      onError: () => toast.error('Failed to update language'),
    });
  };

  if (isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Theme
          </CardTitle>
          <CardDescription>Choose the appearance of the application.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Color Theme</Label>
            <Select value={prefs?.theme ?? 'system'} onValueChange={handleThemeChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {THEMES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {THEMES.map((t) => (
              <button
                key={t.value}
                onClick={() => handleThemeChange(t.value)}
                className={`rounded-md border-2 p-4 text-center text-sm transition-colors ${
                  prefs?.theme === t.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <div className={`mx-auto mb-2 h-8 w-8 rounded-full ${
                  t.value === 'dark' ? 'bg-foreground' : t.value === 'light' ? 'bg-background border border-border' : 'bg-gradient-to-r from-background to-foreground'
                }`} />
                {t.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Language
          </CardTitle>
          <CardDescription>Set the display language for the application.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Display Language</Label>
            <Select value={prefs?.language ?? 'en'} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Full localization support is coming soon. Currently the UI is in English.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
